/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-01 15:00:00
 *
 *******************************************************/

import React, { Component, useEffect } from 'react';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import {
  Card,
  Button,
  ChoiceList,
  TextField,
  Tag,
  Listbox,
  EmptySearchResult,
  Combobox,
  AutoSelection,
  InlineError
} from '@shopify/polaris';
import { useState, useCallback, useMemo } from 'react';
import * as installActions from 'actions/install-actions';
import history from 'src/routes/history';
import { routes } from 'src/routes';
import { isEmail } from 'src/helpers/form-validation-helper';
import * as setupActions from 'actions/setup-actions';
import { prettyText, getShop } from 'src/helpers';

type Props = WithI18nProps;

function MultiselectTypeIssue({ i18n, onSelect, tags = [] }) {
  const [selectedTags, setSelectedTags] = useState(tags);
  const [value, setValue] = useState('');
  const [suggestion, setSuggestion] = useState('');

  const handleActiveOptionChange = useCallback(
    (activeOption: string) => {
      const activeOptionIsAction = activeOption === value;

      if (!activeOptionIsAction && !selectedTags.includes(activeOption)) {
        setSuggestion(activeOption);
      } else {
        setSuggestion('');
      }
    },
    [value, selectedTags]
  );
  const updateSelection = useCallback(
    (selected: string) => {
      const nextSelectedTags = new Set([...selectedTags]);

      if (nextSelectedTags.has(selected)) {
        nextSelectedTags.delete(selected);
      } else {
        nextSelectedTags.add(selected);
      }
      setSelectedTags([...nextSelectedTags]);
      setValue('');
      setSuggestion('');
    },
    [selectedTags]
  );

  const removeTag = useCallback(
    (tag: string) => () => {
      updateSelection(tag);
    },
    [updateSelection]
  );

  const getAllTags = useCallback(() => {
    const savedTags = [
      i18n.translate('AdminHTML.InstallationV2.Survey.step2_select_type_incompatible'),
      i18n.translate('AdminHTML.InstallationV2.Survey.step2_select_type_missing_filter'),
      i18n.translate('AdminHTML.InstallationV2.Survey.step2_select_type_missing_product'),
      i18n.translate('AdminHTML.InstallationV2.Survey.step2_select_type_missing_search'),
      i18n.translate('AdminHTML.InstallationV2.Survey.step2_select_type_missing_recommendation'),
      i18n.translate('AdminHTML.InstallationV2.Survey.step2_select_type_request')
    ];
    return [...new Set([...savedTags, ...selectedTags].sort())];
  }, [selectedTags, i18n]);

  const formatOptionText = useCallback(
    (option: string) => {
      const trimValue = value.trim().toLocaleLowerCase();
      const matchIndex = option.toLocaleLowerCase().indexOf(trimValue);

      if (!value || matchIndex === -1) return option;

      const start = option.slice(0, matchIndex);
      const highlight = option.slice(matchIndex, matchIndex + trimValue.length);
      const end = option.slice(matchIndex + trimValue.length, option.length);

      return (
        <p>
          {start}
          <TextField fontWeight="bold" as="span">
            {highlight}
          </TextField>
          {end}
        </p>
      );
    },
    [value]
  );

  const options = useMemo(() => {
    let list;
    const allTags = getAllTags();
    const filterRegex = new RegExp(value, 'i');

    if (value) {
      list = allTags.filter(tag => tag.match(filterRegex));
    } else {
      list = allTags;
    }

    return [...list];
  }, [value, getAllTags]);

  const verticalContentMarkup =
    selectedTags.length > 0 ? (
      <>
        {selectedTags.map(tag => (
          <Tag key={`option-${tag}`} onRemove={removeTag(tag)}>
            {tag}
          </Tag>
        ))}
      </>
    ) : null;

  const optionMarkup =
    options.length > 0
      ? options.map(option => {
          return (
            <Listbox.Option
              key={option}
              value={option}
              selected={selectedTags.includes(option)}
              accessibilityLabel={option}>
              <Listbox.TextOption selected={selectedTags.includes(option)}>
                {formatOptionText(option)}
              </Listbox.TextOption>
            </Listbox.Option>
          );
        })
      : null;

  const noResults = value && !getAllTags().includes(value);

  const actionMarkup = noResults ? (
    <Listbox.Action value={value}>{`Add "${value}"`}</Listbox.Action>
  ) : null;

  const emptyStateMarkup = optionMarkup ? null : (
    <EmptySearchResult title="" description={`No tags found matching "${value}"`} />
  );

  const listboxMarkup =
    optionMarkup || actionMarkup || emptyStateMarkup ? (
      <Listbox
        autoSelection={AutoSelection.None}
        onSelect={updateSelection}
        onActiveOptionChange={handleActiveOptionChange}>
        {actionMarkup}
        {optionMarkup}
      </Listbox>
    ) : null;

  useEffect(() => {
    onSelect(selectedTags);
  }, [selectedTags, onSelect]);

  return (
    <Combobox
      allowMultiple
      activator={
        <Combobox.TextField
          autoComplete="off"
          label=""
          labelHidden
          value={value}
          suggestion={suggestion}
          placeholder={
            selectedTags.length > 0
              ? ''
              : i18n.translate('AdminHTML.InstallationV2.Survey.step2_select_type_hint')
          }
          verticalContent={verticalContentMarkup}
        />
      }>
      {listboxMarkup}
    </Combobox>
  );
}

class SurveyScreen extends Component<Props> {
  state = {
    currentStep: 1,
    step1Selected: [],
    step2SelectedFeature: [],
    step2SelectedIssues: [],
    step2Detail: '',
    step3Email: ''
  };

  gotoDashboard = () => {
    const { updateInstallationStatus } = this.props;
    updateInstallationStatus('');
    history.push(routes.dashboard.view());
  };

  onSelectIssue = issues => {
    this.setState({ step2SelectedIssues: issues });
  };

  onSubmitSurvey = async data => {
    const { step2SelectedFeature, step2SelectedIssues, step2Detail, step3Email } = this.state;

    const features = {
      filter: 'Filters',
      search: 'Search',
      recommendation: 'Recommendation',
      other: 'Other requests'
    };

    const { sendSetupFeedback } = this.props;

    const prettyEmail = prettyText(step3Email);
    const tenantId = getShop();

    const content = `New onboarding survey
- Store: ${tenantId}
- Email: ${step3Email}
- Feature selected: ${step2SelectedFeature.map(value => features[value]).join(', ')}
- Type of issues: ${step2SelectedIssues.join(', ')}
- Detail: ${step2Detail}
    `;

    const dataForm = {
      email: prettyEmail,
      content,
      featureName: 'New onboarding survey'
    };
    const { updateInstallationStatus } = this.props;
    updateInstallationStatus('');
    await sendSetupFeedback(dataForm);
  };

  componentWillReceiveProps(nextProps) {
    if (!nextProps.loading && this.props.loading) {
      this.setState({ currentStep: 4 });
    }
  }

  render() {
    const { i18n, loading } = this.props;
    const {
      step1Selected,
      currentStep,
      step2SelectedFeature,
      step2SelectedIssues,
      step2Detail,
      step3Email
    } = this.state;

    const Option1 = () => {
      return (
        <div className="option option1">
          <div className="description">
            {i18n.translate('AdminHTML.InstallationV2.Survey.step1_option1_descripiton')}
          </div>
          <div className="btn">
            {step1Selected?.includes('option1') && (
              <Button onClick={this.gotoDashboard} primary>
                {i18n.translate('AdminHTML.InstallationV2.Survey.step1_option1_btn')}
              </Button>
            )}
          </div>
        </div>
      );
    };

    const Option2 = () => {
      return (
        <div className="option option2">
          <div className="description">
            {i18n.translate('AdminHTML.InstallationV2.Survey.step1_option2_descripiton')}
          </div>
          <div className="btn">
            {step1Selected?.includes('option2') && (
              <Button onClick={() => this.setState({ currentStep: 2 })} primary>
                {i18n.translate('AdminHTML.InstallationV2.Survey.step1_option2_btn')}
              </Button>
            )}
          </div>
        </div>
      );
    };

    const choices = [
      {
        value: 'option1',
        label: i18n.translate('AdminHTML.InstallationV2.Survey.step1_option1_title'),
        renderChildren: Option1
      },
      {
        value: 'option2',
        label: i18n.translate('AdminHTML.InstallationV2.Survey.step1_option2_title'),
        renderChildren: Option2
      }
    ];

    const isNextableInStep2 = step2SelectedFeature.length > 0 && step2SelectedIssues.length > 0;
    const isEmailValid = isEmail()(step3Email, null, { i18n }) === undefined;
    const isNextableInStep3 = step3Email !== '' && isEmailValid;

    return (
      <div className="BC-AnotherLayout BC-page_Installation Survey">
        {currentStep === 1 && (
          <Card>
            <div className="BC-page_Installation--Survey">
              <div className="wrapper">
                <div className="top">
                  <div className="title">
                    {i18n.translate('AdminHTML.InstallationV2.Survey.step1_title')}
                  </div>
                  <div className="description">
                    {i18n.translate('AdminHTML.InstallationV2.Survey.step1_description')}
                  </div>
                </div>
                <div className="content">
                  <div className="step1">
                    <ChoiceList
                      title=""
                      choices={choices}
                      selected={step1Selected}
                      onChange={e => this.setState({ step1Selected: e })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}
        {currentStep === 2 && (
          <Card>
            <div className="BC-page_Installation--Survey BC-page_Installation--Survey--Step2">
              <div className="wrapper">
                <div className="top">
                  <div className="title">
                    {i18n.translate('AdminHTML.InstallationV2.Survey.step2_title')}
                  </div>
                  <div className="description">
                    {i18n.translate('AdminHTML.InstallationV2.Survey.step2_description')}
                  </div>
                </div>
                <div className="content">
                  <div className="step2">
                    <div className="select_feature_list">
                      <ChoiceList
                        allowMultiple
                        title={i18n.translate(
                          'AdminHTML.InstallationV2.Survey.step2_select_feature'
                        )}
                        choices={[
                          {
                            label: i18n.translate(
                              'AdminHTML.InstallationV2.Survey.step2_select_filter'
                            ),
                            value: 'filter'
                          },
                          {
                            label: i18n.translate(
                              'AdminHTML.InstallationV2.Survey.step2_select_search'
                            ),
                            value: 'search'
                          },
                          {
                            label: i18n.translate(
                              'AdminHTML.InstallationV2.Survey.step2_select_recommendation'
                            ),
                            value: 'recommendation'
                          },
                          {
                            label: i18n.translate(
                              'AdminHTML.InstallationV2.Survey.step2_select_other'
                            ),
                            value: 'other'
                          }
                        ]}
                        selected={step2SelectedFeature}
                        onChange={e => this.setState({ step2SelectedFeature: e })}
                      />
                      <p style={{ marginTop: '15px' }}>
                        {i18n.translate('AdminHTML.InstallationV2.Survey.step2_select_type')}
                      </p>
                      <div
                        className="multi-select"
                        style={{ marginTop: '5px', marginBottom: '15px' }}>
                        <MultiselectTypeIssue
                          i18n={i18n}
                          onSelect={this.onSelectIssue}
                          tags={step2SelectedIssues}
                        />
                      </div>
                      <TextField
                        label={i18n.translate(
                          'AdminHTML.InstallationV2.Survey.step2_select_detail'
                        )}
                        placeholder={i18n.translate(
                          'AdminHTML.InstallationV2.Survey.step2_select_detail_hint'
                        )}
                        value={step2Detail}
                        onChange={e => this.setState({ step2Detail: e })}
                        multiline={4}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="action">
                <Button onClick={() => this.setState({ currentStep: 1 })}>
                  {i18n.translate('AdminHTML.Common.Action.back')}
                </Button>
                <Button
                  disabled={!isNextableInStep2}
                  primary
                  onClick={() => this.setState({ currentStep: 3 })}>
                  {i18n.translate('AdminHTML.Common.Action.next')}
                </Button>
              </div>
            </div>
          </Card>
        )}
        {currentStep === 3 && (
          <Card>
            <div className="BC-page_Installation--Survey BC-page_Installation--Survey--Step3">
              <div className="wrapper">
                <div className="top">
                  <div className="title">
                    {i18n.translate('AdminHTML.InstallationV2.Survey.step3_title')}
                  </div>
                  <div className="description">
                    {i18n.translate('AdminHTML.InstallationV2.Survey.step3_description')}
                  </div>
                </div>
                <div className="content">
                  <div className="step3">
                    <TextField
                      id="email"
                      type="email"
                      label={i18n.translate('AdminHTML.InstallationV2.Survey.step3_email')}
                      placeholder={i18n.translate(
                        'AdminHTML.InstallationV2.Survey.step3_email_hint'
                      )}
                      value={step3Email}
                      onChange={e => this.setState({ step3Email: e })}
                      error={!isEmailValid}
                    />
                    <InlineError message={isEmail()(step3Email, null, { i18n })} fieldID="email" />
                  </div>
                </div>
              </div>
              <div className="action">
                <Button onClick={() => this.setState({ currentStep: 2 })}>
                  {i18n.translate('AdminHTML.Common.Action.back')}
                </Button>
                <Button
                  disabled={!isNextableInStep3}
                  loading={loading}
                  primary
                  onClick={this.onSubmitSurvey}>
                  {i18n.translate('AdminHTML.Common.Action.submit')}
                </Button>
              </div>
            </div>
          </Card>
        )}
        {currentStep === 4 && (
          <Card>
            <div className="BC-page_Installation--Survey BC-page_Installation--Survey--Step4">
              <div className="wrapper">
                <div className="top">
                  <div className="title">
                    {i18n.translate('AdminHTML.InstallationV2.Survey.step4_title')}
                  </div>
                  <div className="description">
                    {i18n.translate('AdminHTML.InstallationV2.Survey.step4_description', {
                      br: <br />
                    })}
                  </div>
                </div>
                <div className="content">
                  <div className="step4">
                    <Button primary onClick={this.gotoDashboard}>
                      {i18n.translate('AdminHTML.InstallationV2.Survey.step4_btn')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    loading: state.setup.setupFeedbackLoading
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getInstallStatus: () => dispatch(installActions.getInstallStatus(ownProps.i18n)),
  updateInstallationStatus: status => dispatch(installActions.updateInstallationStatus(status)),
  sendSetupFeedback: (body: Object) => dispatch(setupActions.sendSetupFeedback(body, ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(SurveyScreen));
